import type { FC } from 'react';
import { useState } from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import Edit02 from '@untitled-ui/icons-react/build/esm/Edit02';
import BankIcon from "src/icons/untitled-ui/duocolor/bank";
import FileShield02Icon from "src/icons/untitled-ui/duocolor/file-shield-02";
import UsersPlusIcon from "src/icons/untitled-ui/duocolor/users-plus";
import Users03Icon from "src/icons/untitled-ui/duocolor/users-03";
import Home03Icon from "src/icons/untitled-ui/duocolor/home-03";
import { ListItemSecondaryAction } from '@mui/material';
import useHousehold from "src/contexts/household-context";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { paths } from "src/paths";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import { HouseholdContextDialog } from './household-context-dialog';

interface HouseholdSnapshotProps {}

const probateThresholds: Record<string, string> = {
  AL: `Under $25,000`,
  AK: `Under $50,000`,
  AZ: `Under $175,000`,
  AR: `Under $100,000`,
  CA: `Under $184,000`,
  CO: `Under $82,000`,
  CT: `Under $40,000`,
  DE: `Under $30,000`,
  DC: `Under $40,000`,
  FL: `Under $75,000`,
  GA: `Under $10,000`,
  HI: `Under $100,000`,
  ID: `Under $100,000`,
  IL: `Under $100,000`,
  IN: `Under $50,000`,
  IA: `Under $200,000`,
  KS: `Under $75,000`,
  KY: `Under $15,000`,
  LA: `Under $125,000`,
  ME: `Under $40,000`,
  MD: `Under $50,000`,
  MA: `Under $25,000`,
  MI: `Under $25,000`,
  MN: `Under $75,000`,
  MS: `Under $75,000`,
  MO: `Under $40,000`,
  MT: `Under $50,000`,
  NE: `Under $100,000`,
  NV: `Under $25,000`,
  NH: `Under $10,000`,
  NJ: `Under $20,000`,
  NM: `Under $50,000`,
  NY: `Under $50,000`,
  NC: `Under $20,000`,
  ND: `Under $50,000`,
  OH: `Under $35,000`,
  OK: `Under $200,000`,
  OR: `Under $275,000`,
  PA: `Under $50,000`,
  RI: `Under $15,000`,
  SC: `Under $25,000`,
  SD: `Under $50,000`,
  TN: `Under $50,000`,
  TX: `Under $75,000`,
  UT: `Under $100,000`,
  VT: `Under $45,000`,
  VA: `Under $50,000`,
  WA: `Under $100,000`,
  WV: `Under $150,000`,
  WI: `Under $50,000`,
  WY: `Under $200,000`,
};

const probateThresholdSeconds: Record<string, string> = {
  AL: `$25k - $1M`,
  AK: `$50k - $1M`,
  AZ: `$175k - $1M`,
  AR: `$100k - $1M`,
  CA: `$184k - $1M`,
  CO: `$82k - $1M`,
  CT: `$40k - $1M`,
  DE: `$30k - $1M`,
  DC: `$40k - $1M`,
  FL: `$75k - $1M`,
  GA: `$10k - $1M`,
  HI: `$100k - $1M`,
  ID: `$100k - $1M`,
  IL: `$100k - $1M`,
  IN: `$50k - $1M`,
  IA: `$200k - $1M`,
  KS: `$75k - $1M`,
  KY: `$15k - $1M`,
  LA: `$125k - $1M`,
  ME: `$40k - $1M`,
  MD: `$50k - $1M`,
  MA: `$25k - $1M`,
  MI: `$25k - $1M`,
  MN: `$75k - $1M`,
  MS: `$75k - $1M`,
  MO: `$40k - $1M`,
  MT: `$50k - $1M`,
  NE: `$100k - $1M`,
  NV: `$25k - $1M`,
  NH: `$10k - $1M`,
  NJ: `$20k - $1M`,
  NM: `$50k - $1M`,
  NY: `$50k - $1M`,
  NC: `$20k - $1M`,
  ND: `$50k - $1M`,
  OH: `$35k - $1M`,
  OK: `$200k - $1M`,
  OR: `$275k - $1M`,
  PA: `$50k - $1M`,
  RI: `$15k - $1M`,
  SC: `$25k - $1M`,
  SD: `$50k - $1M`,
  TN: `$50k - $1M`,
  TX: `$75k - $1M`,
  UT: `$100k - $1M`,
  VT: `$45k - $1M`,
  VA: `$50k - $1M`,
  WA: `$100k - $1M`,
  WV: `$150k - $1M`,
  WI: `$50k - $1M`,
  WY: `$200k - $1M`,
};

/** Net-worth buckets */
export const netWorthOptions: Record<string, string> = {
  "": "",
  "exempt": "",
  "up_to_one": "25k to 1M",
  "up_to_five": "$1M - $5M",
  "up_to_ten": "$5 - $10M",
  "over_ten": "Over $10M"
};

/** Marital / partnership status */
export const maritalStatusOptions: Record<string, string> = {
  "": "",
  single: "Single",
  married: "Married",
  domestic_partnership: "Domestic Partnership",
  civil_union: "Civil Union"
};

/** Children situation */
export const childrenOptions: Record<string, string> = {
  "": "",
  none: "No Children",
  adult: "Adult Children",
  minor: "Minor Children"
};

/** Blended-family flag */
export const blendedFamilyOptions: Record<string, string> = {
  "": "",
  blended: "(blended)",
  not: ""
};

/** Estate-plan foundation choice */
export const estatePlanFoundationOptions: Record<string, string> = {
  "": "",
  attorney: "Attorney",
  joint: "Joint Trust",
  trust: "Individual Trust",
  will: "Individual Will"
};

/** State picker (abbreviation → full name) */
export const statesOptions: Record<string, string> = {
  "": "",
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia"
};

export const HouseholdSnapshot: FC<HouseholdSnapshotProps> = (
  props
) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const { selectedHousehold } = useHousehold();
  const hOH = selectedHousehold?.head_of_household;
  const household_state_code = hOH?.household_state_code;
  const children = hOH?.children;
  const blended_family = hOH?.blended_family;
  const estate_plan_foundation = hOH?.estate_plan_foundation;
  const net_worth = hOH?.value_of_assets;

  const navigate = useNavigate();
  
  // Get spouse role for marital status
  const { peopleRoles } = usePeopleRolesStore(hOH?.ud_id || null);
  const spouse_role = peopleRoles?.find((role: any) => role.role === 'SPOUSE');

  const marital_status = spouse_role?.spouse_relationship || 
    ((!spouse_role && (children || net_worth || household_state_code || estate_plan_foundation)) ? 'single' : undefined);

  function pickCopy(): string {
    if (net_worth === 'exempt') {                // IF  ➜ use primaryCopy
      return probateThresholds[household_state_code as keyof typeof probateThresholds] || '?';
    } else if (net_worth === 'up_to_one') {         // ELSE IF ➜ use secondaryCopy
      return probateThresholdSeconds[household_state_code as keyof typeof probateThresholdSeconds] || '?';
    }                                // ELSE ➜ use fallbackCopy
    return netWorthOptions[net_worth as keyof typeof netWorthOptions] || '?';
  }

  return (
    <>
      <Card variant="outlined"
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        backgroundColor: "#fff",
        border: '1px solid #aaa'
      }}>
      <CardHeader
        title="Family Snapshot"
        action={(
          <>
          {estate_plan_foundation && estate_plan_foundation !== 'skip' ?
            <IconButton color="inherit" onClick={handleOpen}>
            <SvgIcon>
              <Edit02 />
            </SvgIcon>
          </IconButton>
          :
          <IconButton color="inherit" onClick={() => navigate(paths.dashboard.onboarding)}>
            <SvgIcon>
              <Edit02 />
            </SvgIcon>
          </IconButton>
            }
          </>
        )}

      />
      <List disablePadding sx={{ pb: 2, px: 1 }}>

        <ListItem
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              cursor: 'pointer'
            }
          }}
          onClick={handleOpen}
        >
          <ListItemAvatar>

            <SvgIcon fontSize="medium">
              <Users03Icon />
            </SvgIcon>

          </ListItemAvatar>
          <ListItemText
            disableTypography
            primary={(
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="subtitle2"
              >
                {maritalStatusOptions[marital_status as keyof typeof maritalStatusOptions] || '?'}
              </Typography>
            )}
            secondary={(
              <Typography
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                Marital Status
              </Typography>
            )}
            sx={{ pr: 2 }}
          />

        </ListItem>
        <Divider />
        <ListItem
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              cursor: 'pointer'
            }
          }}
          onClick={handleOpen}
        >
          <ListItemAvatar>

            <SvgIcon fontSize="medium">
              <UsersPlusIcon />
            </SvgIcon>

          </ListItemAvatar>
          <ListItemText
            disableTypography
            primary={(
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="subtitle2"
              >
                {childrenOptions[children as keyof typeof childrenOptions] || '?'}{blended_family && children && !['', 'none'].includes(children || '') && ' (blended)'}
              </Typography>
            )}
            secondary={(
              <Typography
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                Children
              </Typography>
            )}
            sx={{ pr: 2 }}
          />

        </ListItem>
        <Divider />
        <ListItem
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              cursor: 'pointer'
            }
          }}
          onClick={handleOpen}
        >
          <ListItemAvatar>

            <SvgIcon fontSize="medium">
              <Home03Icon />
            </SvgIcon>

          </ListItemAvatar>
          <ListItemText
            disableTypography
            primary={(
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="subtitle2"
              >
                {statesOptions[household_state_code as keyof typeof statesOptions] || '?'}
              </Typography>
            )}
            secondary={(
              <Typography
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                State of Residence
              </Typography>
            )}
            sx={{ pr: 2 }}
          />

        </ListItem>

        <Divider />
        <ListItem
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              cursor: 'pointer'
            }
          }}
          onClick={handleOpen}
        >
          <ListItemAvatar>

            <SvgIcon fontSize="medium">
              <BankIcon />
            </SvgIcon>

          </ListItemAvatar>
          <ListItemText
            disableTypography
            primary={(
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="subtitle2"
              >
                {pickCopy()}
              </Typography>
            )}
            secondary={(
              <Typography
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                Value of Assets
              </Typography>
            )}
            sx={{ pr: 2 }}
          />

        </ListItem>

        <Divider />
        <ListItem
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              cursor: 'pointer'
            }
          }}
          onClick={handleOpen}
        >
          <ListItemAvatar>

            <SvgIcon fontSize="medium">
              <FileShield02Icon />
            </SvgIcon>

          </ListItemAvatar>
          <ListItemText
            disableTypography
            primary={(
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="subtitle2"
              >
                {estatePlanFoundationOptions[estate_plan_foundation as keyof typeof estatePlanFoundationOptions] || '?'}{marital_status !== 'single' && (estate_plan_foundation === 'will' || estate_plan_foundation === 'trust') && `s`}
              </Typography>
            )}
            secondary={(
              <Typography
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                Estate Plan Strategy
              </Typography>
            )}
            sx={{ pr: 2 }}
          />
          <ListItemSecondaryAction></ListItemSecondaryAction>
        </ListItem>
      </List>
      {(!estate_plan_foundation || estate_plan_foundation === 'skip') &&
      <Box sx={{p:2, pt:0}}>
        <Button variant='contained' fullWidth endIcon={<AutoAwesomeIcon fontSize='small'/>}
        onClick={() => navigate(paths.dashboard.onboarding)}
        >
        Get your personal plan
        </Button>
      </Box>
      }
      </Card>
    
      <HouseholdContextDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

HouseholdSnapshot.propTypes = {

};
